import React, { useMemo } from 'react';
import * as finderActions from '../actions';
import {
  Asset as AssetType,
  AssetPerm,
  isAssetDownloadAllowed,
  isLicensedFileAsset,
} from '../../../api';
import { ShieldSecurity } from '@myua/icons/dist/bulk16';
import { useDispatch, useSelector } from 'react-redux';
import { getAssetDimensions, makeGetAssetById, makeGetIsAssetSelected } from '../selectors';
import { FileOutlinedWithExtension } from '../../../../general/components/FullscreenPreview/viewers/MediaUnknown';
import { generatePath, useHistory } from 'react-router-dom';
import { Page, routes } from '../../router/model';
import { getPage } from '../../router/selectors';
import { PackToggle } from '../../pack/components/PackToggle';
import { Thumbnail } from '../../../../general/components/Thumbnail/Thumbnail';
import { getIsPublicDownload } from '../../sharing/selectors';
import { isLikeCtrlKey } from '../model';
import { getViewMode } from '../../viewMode/selectors';
import { ViewMode } from '../../viewMode/model';
import * as viewModeActions from '../../viewMode/actions';
import * as viewerActions from '../../viewer/actions';
import { NoDownload } from '../../icons/NoDownload';
import classNames from 'classnames';

export const Asset: React.FC<{ id: AssetType['id'] }> = React.memo(({ id }) => {
  const dispatch = useDispatch();
  const getAssetById = useMemo(() => makeGetAssetById(id), [id]);
  const asset = useSelector(getAssetById);
  const history = useHistory();
  const page = useSelector(getPage);
  const getIsAssetSelected = useMemo(() => makeGetIsAssetSelected(id), [id]);
  const isSelected = useSelector(getIsAssetSelected);
  const viewMode = useSelector(getViewMode);
  const isCreative = page === Page.viewer && viewMode === ViewMode.creatives;
  const { assetWidth, assetHeight } = useSelector(getAssetDimensions);
  const isPublicDownload = useSelector(getIsPublicDownload);

  if (!asset) {
    return null;
  }
  const fallback = <FileOutlinedWithExtension filename={asset.name} />;
  // Бейдж «под модерацией» в каталоге (MYUA-16456). Список ассетов не содержит объектов
  // лицензий, поэтому опираемся на флаг бэкенда: файл, который может нести лицензию
  // (шрифт или архив со шрифтами внутри), с запрещённым скачиванием — под модерацией.
  const isUnderModeration = isLicensedFileAsset(asset) && !isAssetDownloadAllowed(asset);
  return (
    <div
      className={classNames('al-finder-asset', {
        'al-finder-asset_selected': isSelected,
        'al-finder-asset_creative': isCreative,
      })}
      style={{ width: assetWidth, height: assetHeight }}
      onClick={(e) => {
        e.stopPropagation();

        if (e.detail === 1) {
          // single click
          dispatch(
            finderActions.assetClicked({
              id: asset.id,
              shiftKey: e.shiftKey,
              ctrlKey: isLikeCtrlKey(e),
            }),
          );
        }

        if (e.detail === 2) {
          // double click
          history.push(
            isCreative
              ? generatePath(routes.previewCreative, { assetID: asset.id })
              : generatePath(routes.preview, { assetID: asset.id }),
          );
        }
      }}
    >
      {asset.is_new ? <div className="al-finder-asset__new">New</div> : null}
      {!isCreative && asset.type.extension ? (
        <div
          className="al-finder-asset__extension"
          title={asset.type.extension}
          style={{
            backgroundColor: asset.type.bg_color || undefined,
            color: asset.type.font_color || undefined,
          }}
        >
          {asset.type.extension}
        </div>
      ) : null}
      {!isCreative && <PackToggle asset={asset} />}
      {isPublicDownload || asset.perms.includes(AssetPerm.download) || isCreative ? null : (
        <div className="al-finder-asset__no-download" title="No download permissions">
          <NoDownload />
        </div>
      )}
      {!isCreative && isUnderModeration ? (
        <div className="al-finder-asset__under-moderation" title="File is under legal moderation">
          <ShieldSecurity />
        </div>
      ) : null}
      <div className="al-finder-asset__image-slot">
        {asset.thumbnail_url ? (
          <Thumbnail
            className="al-finder-asset__image"
            alt={asset.name}
            src={asset.thumbnail_url}
            fallback={fallback}
            retry={false}
            fadeIn
          />
        ) : (
          fallback
        )}
      </div>
      <div
        className="al-finder-asset__name"
        onClick={
          isCreative
            ? (e) => {
                e.stopPropagation();
                dispatch(
                  viewerActions.creativeChanged({ creative: { id: asset.id, name: asset.name } }),
                );
                dispatch(viewModeActions.changed({ viewMode: ViewMode.assets }));
              }
            : undefined
        }
      >
        {asset.name}
      </div>
    </div>
  );
});
